import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Button, Col, Input, Label, Row, Spinner } from "reactstrap";

import { isSlotInPast, todayIsoDate } from "../../../helpers/agendaDateTime";
import {
  getPublicBookingAvailability,
  PublicBookingAvailabilitySlot,
  PublicBookingProfessional,
  PublicBookingService,
} from "../../../services/publicBookingService";
import BookingCatalogSkeleton from "./BookingCatalogSkeleton";

interface DateTimeStepProps {
  token: string;
  service: PublicBookingService;
  professional: PublicBookingProfessional;
  selectedSlot: PublicBookingAvailabilitySlot | null;
  onSelectSlot: (slot: PublicBookingAvailabilitySlot | null) => void;
  onBack: () => void;
}

const formatTimeLabel = (datetime: string): string => datetime.slice(11, 16);

const slotKey = (slot: PublicBookingAvailabilitySlot): string =>
  `${slot.start_datetime}|${slot.end_datetime}`;

const DateTimeStep: React.FC<DateTimeStepProps> = ({
  token,
  service,
  professional,
  selectedSlot,
  onSelectSlot,
  onBack,
}) => {
  const { t } = useTranslation();
  const [selectedDate, setSelectedDate] = useState(todayIsoDate());
  const [slots, setSlots] = useState<PublicBookingAvailabilitySlot[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!token || !service.id || !professional.id) {
      return;
    }

    let cancelled = false;
    setLoading(true);
    setLoadError(null);

    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const response = await getPublicBookingAvailability(token, professional.id, {
            date: selectedDate,
            service_id: service.id,
          });

          if (!cancelled) {
            setSlots(response.data ?? []);
          }
        } catch {
          if (!cancelled) {
            setSlots([]);
            setLoadError(t("public_booking.errors.loadAvailability"));
          }
        } finally {
          if (!cancelled) {
            setLoading(false);
          }
        }
      })();
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [token, service.id, professional.id, selectedDate, t]);

  const visibleSlots = useMemo(
    () => slots.filter((slot) => !isSlotInPast(slot.start_datetime)),
    [slots]
  );

  const emptyReason = useMemo((): "allPast" | "noSlots" | null => {
    if (loading || loadError) {
      return null;
    }

    if (slots.length > 0 && visibleSlots.length === 0) {
      return "allPast";
    }

    if (slots.length === 0) {
      return "noSlots";
    }

    return null;
  }, [loadError, loading, slots.length, visibleSlots.length]);

  return (
    <>
      <div className="d-flex align-items-start justify-content-between gap-3 mb-4">
        <div>
          <p className="text-muted mb-1">{t("public_booking.datetime.selectedProfessional")}</p>
          <h5 className="mb-0">{professional.name}</h5>
          <p className="text-muted mb-0 mt-1">{service.name}</p>
        </div>
        <Button color="light" size="sm" onClick={onBack}>
          <i className="ri-arrow-left-line align-bottom me-1" />
          {t("public_booking.common.back")}
        </Button>
      </div>

      <div className="mb-4">
        <Label for="public-booking-date" className="form-label">
          {t("public_booking.datetime.dateLabel")}
        </Label>
        <Input
          id="public-booking-date"
          type="date"
          value={selectedDate}
          min={todayIsoDate()}
          onChange={(event) => {
            setSelectedDate(event.target.value);
            onSelectSlot(null);
          }}
        />
      </div>

      {loadError ? (
        <Alert color="danger" className="mb-3">
          {loadError}
        </Alert>
      ) : null}

      {loading ? (
        <>
          <p className="text-muted mb-3">{t("public_booking.datetime.loading")}</p>
          <BookingCatalogSkeleton count={3} />
        </>
      ) : emptyReason === "allPast" ? (
        <Alert color="warning" className="mb-0">
          {t("public_booking.datetime.emptyAllPast")}
        </Alert>
      ) : emptyReason === "noSlots" ? (
        <div className="text-center py-4">
          <div className="avatar-md mx-auto mb-3">
            <span className="avatar-title rounded-circle bg-light text-muted fs-24">
              <i className="ri-calendar-line" />
            </span>
          </div>
          <h5 className="mb-2">{t("public_booking.datetime.emptyTitle")}</h5>
          <p className="text-muted mb-0">{t("public_booking.datetime.emptyDescription")}</p>
        </div>
      ) : (
        <Row className="g-2">
          {visibleSlots.map((slot) => {
            const isSelected = selectedSlot ? slotKey(selectedSlot) === slotKey(slot) : false;

            return (
              <Col xs={6} sm={4} md={3} key={slotKey(slot)}>
                <Button
                  color={isSelected ? "primary" : "light"}
                  className={`w-100${isSelected ? "" : " border"}`}
                  onClick={() => onSelectSlot(slot)}
                >
                  {formatTimeLabel(slot.start_datetime)}
                </Button>
              </Col>
            );
          })}
        </Row>
      )}

      {loading ? (
        <div className="text-center mt-3">
          <Spinner size="sm" color="primary" />
        </div>
      ) : null}
    </>
  );
};

export default DateTimeStep;
